/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "validate.js";
import { showSnackbar } from "../../app/slices/commonSlice";
import {
  fetchUploads,
  resetStateUploads,
  updateUpload,
  uploadData,
} from "../../app/slices/uploadSlice";
import {
  dateFormat,
  getBase64,
  hasAccess,
  translateStatus,
  uploadTypes,
  getObjectDifference,
} from "../../app/utils";
import AppDataTable from "../../components/appDataTable/appDataTable";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import EmptyDataContainer from "../../components/emptyDataContainer/emptyDataContainer";
import ErrorContainer from "../../components/errorContainer/errorContainer";
import LoadingContainer from "../../components/loadingContainer/loadingContainer";
import ViewAttachmentDialog from "../../components/ViewAttachmentDialog";
import ManageUploads from "./manageUploads";
import React from "react";

const Uploads = ({ status }) => {
  const { activeSession } = useSelector(({ auth }) => auth);
  const {
    list,
    status: statusList,
    error: errorList,
  } = useSelector(({ uploads }) => uploads.get);

  const {
    data: dataPost,
    status: statusPost,
    error: errorPost,
  } = useSelector(({ uploads }) => uploads.post);

  const {
    data: dataPut,
    status: statusPut,
    error: errorPut,
  } = useSelector(({ uploads }) => uploads.put);

  const columns = [
    { name: "UPLOAD_ID", label: "Document ID", options: { visible: false } },
    { name: "AUTHOR_NAME", label: "Author" },
    { name: "TITLE_DISPLAY", label: "Title" },
    { name: "DESC_DISPLAY", label: "Description" },
    { name: "TYPE_DESC", label: "Type" },
    { name: "DATE_ADDED", label: "Date Added" },
    { name: "FEATURED_DISPLAY", label: "Featured" },
    {
      name: "DISPLAY_STATUS",
      label: "Status",
      options: { visible: activeSession !== null },
    },
  ];

  const validationSchema = {
    title_sw: { presence: { allowEmpty: false } },
    title_en: { presence: { allowEmpty: false } },
    type: { presence: { allowEmpty: false } },
  };

  const validationBooks = {
    type: { presence: { allowEmpty: false } },
    edition: { presence: { allowEmpty: false } },
    publisher: { presence: { allowEmpty: false } },
    placeOfPublication: { presence: { allowEmpty: false } },
    publicationYear: { presence: { allowEmpty: false } },
    isbn: { 
      presence: { allowEmpty: false },
      length: {
        is: 13,
        message: "must be exactly 13 characters"
      }
    },
    subjects_en: { presence: { allowEmpty: false } },
    subjects_sw: { presence: { allowEmpty: false } },
    summary_en: { presence: { allowEmpty: false } },
    summary_sw: { presence: { allowEmpty: false } },
  };

  const validationJournals = {
    category: { presence: { allowEmpty: false } },
  };

  const [errors, setErrors] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [openConfirmRemove, setOpenConfirmRemove] = useState(false);
  const [data, setData] = useState([]);
  const [openViewFile, setOpenViewFile] = useState(false);
  const [open, setOpen] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const dispatch = useDispatch();
  const [language] = useState(
    localStorage.getItem("currentLanguage")?.toUpperCase(),
  );

  const getPayload = () => ({
    TITLE_EN: formValues.title_en,
    SUB_TITLE_EN: formValues.subTitle_en,
    TITLE_SW: formValues.title_sw,
    SUB_TITLE_SW: formValues.subTitle_sw,
    CATEGORY: formValues.category,
    FEATURED: formValues.featured,
    GENRE_ID: formValues?.genre,
    TYPE: formValues.type,
    AUTHOR_ID: formValues.authorId,
    EDITION: formValues.edition,
    PUBLISHER: formValues.publisher,
    PLACE_OF_PUBLICATION: formValues.placeOfPublication,
    PUBLICATION_DATE: formValues.publicationDate,
    PUBLICATION_YEAR: formValues.publicationYear?.toString(),
    ISBN: formValues.isbn?.trim(),
    ISSN: formValues.issn?.trim(),
    SUBJECTS_EN: formValues.subjects_en,
    SUBJECTS_SW: formValues.subjects_sw,
    SERIES: formValues.series,
    VOLUME_ISSUE: formValues.volumeIssue,
    DURATION: formValues.duration,
    SUMMARY_EN: formValues.summary_en,
    SUMMARY_SW: formValues.summary_sw,
    DESCRIPTION_EN: formValues.description_en,
    DESCRIPTION_SW: formValues.description_sw,
  });

  const getInitialPayload = () =>
    selectedUpload
      ? {
          TITLE_EN: selectedUpload.TITLE_EN,
          SUB_TITLE_EN: selectedUpload.SUB_TITLE_EN,
          TITLE_SW: selectedUpload.TITLE_SW,
          SUB_TITLE_SW: selectedUpload.SUB_TITLE_SW,
          CATEGORY: selectedUpload.CATEGORY,
          FEATURED: selectedUpload.FEATURED,
          GENRE_ID: selectedUpload.GENRE_ID,
          TYPE: selectedUpload.TYPE,
          AUTHOR_ID: selectedUpload.AUTHOR_ID,
          EDITION: selectedUpload.EDITION,
          PUBLISHER: selectedUpload.PUBLISHER,
          PLACE_OF_PUBLICATION: selectedUpload.PLACE_OF_PUBLICATION,
          PUBLICATION_DATE: selectedUpload.PUBLICATION_DATE,
          PUBLICATION_YEAR: selectedUpload.PUBLICATION_YEAR,
          ISBN: selectedUpload.ISBN,
          ISSN: selectedUpload.ISSN,
          SUBJECTS_EN: selectedUpload.SUBJECTS_EN,
          SUBJECTS_SW: selectedUpload.SUBJECTS_SW,
          SERIES: selectedUpload.SERIES,
          VOLUME_ISSUE: selectedUpload.VOLUME_ISSUE,
          DURATION: selectedUpload.DURATION,
          SUMMARY_EN: selectedUpload.SUMMARY_EN,
          SUMMARY_SW: selectedUpload.SUMMARY_SW,
          DESCRIPTION_EN: selectedUpload.DESCRIPTION_EN,
          DESCRIPTION_SW: selectedUpload.DESCRIPTION_SW,
        }
      : {};

  const hasChanges = React.useMemo(() => {
    if (!selectedUpload) return true;
    const diff = getObjectDifference(getInitialPayload(), getPayload());
    const hasDocChanges = Boolean(
      formValues.document || formValues.documentSW || formValues.newCoverImage,
    );
    return Object.keys(diff).length > 0 || hasDocChanges;
  }, [formValues, selectedUpload]);

  useEffect(() => {
    dispatch(resetStateUploads());
    loadResources();
    return () => dispatch(resetStateUploads());
  }, [status]);

  useEffect(() => {
    if (list) {
      const _data = list.map((item) => ({
        ...item,
        DATE_ADDED: item.CREATED_AT ? moment(item.CREATED_AT).format(dateFormat) : "N/A",
        TYPE_DESC: uploadTypes.find((type) => type.value === item.TYPE)?.label,
        TITLE_DISPLAY: item[`TITLE_${language}`],
        DESC_DISPLAY: item[`DESCRIPTION_${language}`],
        FEATURED_DISPLAY: item?.FEATURED ? "Yes" : "No",
        DISPLAY_STATUS: translateStatus(item?.STATUS),
      }));
      setData(_data);
    }
  }, [list]);

  useEffect(() => {
    if (dataPost || dataPut) {
      dispatch(
        showSnackbar({
          open: true,
          message: `Upload ${dataPost ? "uploaded" : "updated"} successfully.`,
          type: "success",
        }),
      );
      resetForm();
      setTimeout(loadResources, 100);
    }
  }, [dataPost, dataPut]);

  useEffect(() => {
    if (errors?.swDocument || errors.enDocument || errorPost || errorPut) {
      dispatch(
        showSnackbar({
          open: true,
          message:
            errors?.enDocument?.[0] ||
            errors?.swDocument?.[0] ||
            errorPost?.message ||
            errorPut?.message ||
            "An error occurred.",
          type: "error",
        }),
      );

      if (errorPost || errorPut)
        dispatch(resetStateUploads([errorPost ? "post" : "put"]));
    }
  }, [errors, errorPost, errorPut]);

  const loadResources = () => {
    dispatch(fetchUploads({ TYPE: status, STATUS: "A", ignoreLanguage: "Y" }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    let inputValue = value;

    if (name === "type") {
      setErrors([]);
    }

    if (files?.length) {
      if (files[0]?.size > 35000000) {
        dispatch(
          showSnackbar({
            open: true,
            type: "error",
            message: "Upload should not exceed 35 megabytes in size",
          }),
        );
        return;
      }
      inputValue = files[0];
    }

    if (
      name === "edition" ||
      name === "series" ||
      name === "volumeIssue" ||
      name === "duration" ||
      name === "isbn"
    ) {
      const re = /^[0-9,]*$/;
      if (inputValue === "" || re.test(inputValue)) {
        setFormValues((prevState) => ({ ...prevState, [name]: inputValue }));
      }
    } else {
      setFormValues((prevState) => ({ ...prevState, [name]: inputValue }));
    }
  };

  const getValidationSchema = () => {
    switch (formValues?.type) {
      case "eBook":
        return { ...validationSchema, ...validationBooks };
      case "journal":
        return { ...validationSchema, ...validationJournals };
      default:
        return validationSchema;
    }
  };

  const handleUpload = async () => {
    const _errors = validate(formValues, getValidationSchema());

    if (_errors) {
      setErrors(_errors);
      return;
    }

    setErrors([]);
    setIsPreparing(true);

    try {
      const documentData = {};
      if (formValues.document) {
        documentData.DATA = await getBase64(formValues.document);
      }

    if (formValues.documentSW) {
      documentData.DATA_SW = await getBase64(formValues.documentSW);
    }
    if (formValues.newCoverImage) {
      documentData.COVER_IMAGE = await getBase64(formValues.newCoverImage);
    }

    const basePayload = getPayload();
    const finalPayload = selectedUpload
      ? {
          ...getObjectDifference(getInitialPayload(), basePayload),
          ...documentData,
        }
      : { ...basePayload, ...documentData };

      dispatch(
        selectedUpload
          ? updateUpload({ id: selectedUpload.UPLOAD_ID, payload: finalPayload })
          : uploadData(finalPayload),
      );
    } finally {
      setIsPreparing(false);
    }
  };

  const resetForm = () => {
    setErrors([]);
    setFormValues({});
    setSelectedUpload(null);
    setOpen(false);
    dispatch(resetStateUploads([dataPost ? "post" : "put"]));
  };

  const BuildTable = () => {
    if (statusList === "loading")
      return <LoadingContainer message="Loading Uploads..." />;
    if (errorList)
      return (
        <ErrorContainer onReload={loadResources} message={errorList?.message} />
      );
    if (list?.length === 0 && statusList === "idle")
      return (
        <EmptyDataContainer message="No uploads added" hasBackground={false} />
      );
    if (data?.length > 0 && statusList === "idle") {
      return (
        <AppDataTable
          id="documents-list"
          actions={[
            {
              action: "View",
              onClick: (item) => {
                setSelectedUpload(item);
                setOpen(true);
              },
            },
            {
              action: "Remove",
              onClick: (item) => {
                setSelectedUpload(item);
                setOpenConfirmRemove(true);
              },
              renderCheck: () =>
                hasAccess({ resource: "MANAGE UPLOADS", type: "resource" }),
            },
          ]}
          columns={columns}
          data={data}
          emptyMessage="No uploads found."
          rowsPerPageOptions={[10, 20, 50, 100]}
          downloadFileName="Uploads"
        />
      );
    }
  };

  const getRequiredFields = () => {
    const schema = getValidationSchema();
    return Object.keys(schema);
  };

  return (
    <div className="app-padding">
      <div className="flex-row mt-20">
        {hasAccess({ resource: "MANAGE UPLOADS", type: "resource" }) && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Upload document
          </Button>
        )}
      </div>

      {BuildTable()}

      {openConfirmRemove && selectedUpload && (
        <ConfirmationDialog
          onClose={() => setOpenConfirmRemove(false)}
          open={openConfirmRemove}
          message={`Are you sure you want to remove file "${
            selectedUpload[`TITLE_${language}`]
          }"`}
          title="Confirm Upload Removal"
          handleResponse={() => {
            dispatch(
              updateUpload({
                id: selectedUpload.UPLOAD_ID,
                payload: { STATUS: "inactive" },
              }),
            );
            setSelectedUpload(null);
            setOpenConfirmRemove(false);
          }}
        />
      )}

      {openViewFile && selectedUpload && (
        <ViewAttachmentDialog
          upload={selectedUpload}
          handleClose={() => {
            setOpenViewFile(false);
            setSelectedUpload(null);
          }}
          open={openViewFile}
        />
      )}

      {open && (
        <Dialog
          open={open}
          fullWidth
          maxWidth="lg"
          disableBackdropClick
          disableEscapeKeyDown
          onClose={() => {
            setOpen(false);
            setSelectedUpload(null);
            loadResources();
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{ fontWeight: "bold" }}>
            {selectedUpload ? "Update" : "Upload"} details
          </DialogTitle>
          <DialogContent>
            <ManageUploads
              formValues={formValues}
              setFormValues={setFormValues}
              handleChange={handleChange}
              errors={errors}
              data={selectedUpload}
              requiredFields={getRequiredFields()}
            />
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              variant="contained"
              disabled={(statusPost || statusPut) === "loading" || isPreparing || !hasChanges}
              onClick={handleUpload}
            >
              {((statusPost || statusPut) === "loading" || isPreparing) && (
                <CircularProgress
                  color="primary"
                  size={17}
                  style={{ marginRight: "8px" }}
                />
              )}
              {selectedUpload ? "Update" : "Upload"}
            </Button>
            <Button
              onClick={() => {
                loadResources();
                setOpen(false);
              }}
              style={{ marginRight: "8px" }}
              variant="contained"
              color="secondary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default Uploads;
